// components/ui/StyledSelect.jsx
// Drop-in replacement for the native <select> used across the app, built on
// Radix Select for accessibility (keyboard nav, type-ahead), a portaled popup
// (never clipped inside modals/panels) and the app's dark/glass theme.
//
// API mirrors the native element so migrations are mechanical:
//   <StyledSelect
//     value={config.operation}
//     onChange={(v) => onFieldChange('operation', v)}
//     options={[{ value: 'select', label: 'Select' }, ...]}
//     placeholder="Choose an operation"
//   />
//
// `options` accepts {value, label, disabled} objects or plain strings (which
// become {value: s, label: s}). Empty-string values (the classic "— none —"
// option) are supported via an internal sentinel, so selects that let the user
// clear their choice keep working.
import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "../../lib/utils";

// Radix forbids an item with an empty-string value; map "" to this sentinel so
// callers can still express a clearable / "none" option transparently.
const EMPTY = " __empty__";
const toRadix = (v) => (v === "" ? EMPTY : v);
const fromRadix = (v) => (v === EMPTY ? "" : v);

const normalizeOptions = (options = []) =>
  options.map((o) =>
    typeof o === "string" || typeof o === "number"
      ? { value: String(o), label: String(o) }
      : o
  );

const triggerBase =
  "flex w-full items-center justify-between gap-2 rounded-xl border border-white/10 " +
  "bg-black/40 px-4 py-3 text-sm text-white transition-colors " +
  "hover:border-white/20 focus:outline-none focus:border-violet-500/50 " +
  "focus:ring-2 focus:ring-violet-500/20 disabled:cursor-not-allowed disabled:opacity-50 " +
  "data-[placeholder]:text-gray-400 [&>span]:line-clamp-1 [&>span]:text-left";

export default function StyledSelect({
  value,
  defaultValue,
  onChange,
  options,
  children,
  placeholder = "Select...",
  disabled = false,
  searchable = false,          // render a filter input at the top of the popup
  searchPlaceholder = "Search...",
  toolbar = null,              // optional node rendered at the very top of the popup

  className,        // applied to the trigger (so existing className strings keep working)
  triggerClassName, // alias, takes precedence if provided
  contentClassName,
  ...props
}) {
  const [query, setQuery] = React.useState("");
  const opts = normalizeOptions(options);
  // Filter by the search query, preserving group structure. A group survives
  // only if it still has matching children.
  const q = query.trim().toLowerCase();
  const matches = (o) => o.label != null && String(o.label).toLowerCase().includes(q);
  const visibleOpts = !q
    ? opts
    : opts.flatMap((o) => {
        if (Array.isArray(o.options)) {
          const kids = o.options.filter(matches);
          return kids.length ? [{ ...o, options: kids }] : [];
        }
        return matches(o) ? [o] : [];
      });
  // A group is an entry with its own `options` array; flatten to reason about
  // the actual selectable items (empty-option detection).
  const flatOpts = opts.flatMap((o) => (Array.isArray(o.options) ? o.options : [o]));
  // Only treat an empty value as a real selection (the sentinel item) when the
  // caller actually offers an empty-value option (e.g. SchemaForm's "— none —").
  // Otherwise keep "" so Radix renders the placeholder instead of a blank box.
  const hasEmptyOption = flatOpts.some((o) => String(o.value) === "");
  const mapValue = (v) =>
    v === undefined || v === null ? undefined : v === "" ? (hasEmptyOption ? EMPTY : "") : v;

  return (
    <SelectPrimitive.Root
      value={mapValue(value)}
      defaultValue={mapValue(defaultValue)}
      onValueChange={(v) => onChange?.(fromRadix(v))}
      onOpenChange={(open) => { if (!open) setQuery(""); }}
      disabled={disabled}
      {...props}
    >
      <SelectPrimitive.Trigger className={cn(triggerBase, triggerClassName || className)}>
        <SelectPrimitive.Value placeholder={placeholder} />
        <SelectPrimitive.Icon asChild>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={6}
          className={cn(
            "relative z-[60] max-h-72 min-w-[var(--radix-select-trigger-width)] overflow-hidden " +
              "rounded-xl border border-white/10 bg-[#16141f]/95 text-white shadow-xl " +
              "shadow-black/40 backdrop-blur-md " +
              "data-[state=open]:animate-in data-[state=closed]:animate-out " +
              "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 " +
              "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            contentClassName
          )}
          onOpenAutoFocus={(e) => { if (searchable) e.preventDefault(); }}
        >
          {toolbar && (
            <div
              className="border-b border-white/10 p-1.5"
              // Keep clicks inside the toolbar from reaching Radix (which would
              // otherwise treat them as a selection/close gesture).
              onPointerDown={(e) => e.stopPropagation()}
            >
              {toolbar}
            </div>
          )}
          {searchable && (
            <div className="border-b border-white/10 p-1.5">
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  // Let Radix handle nav/close (arrows, enter, escape); stop the
                  // other keys so its built-in type-ahead doesn't steal what we
                  // type into the search box.
                  if (!["ArrowDown", "ArrowUp", "Enter", "Escape", "Tab"].includes(e.key)) {
                    e.stopPropagation();
                  }
                }}
                placeholder={searchPlaceholder}
                className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white placeholder-gray-500 focus:border-violet-500/50 focus:outline-none"
              />
            </div>
          )}
          <SelectPrimitive.Viewport className="p-1">
            {visibleOpts.length > 0
              ? visibleOpts.map((o, i) =>
                  Array.isArray(o.options) ? (
                    <SelectPrimitive.Group key={o.label || i}>
                      <SelectPrimitive.Label
                        className="mt-2 mb-1 first:mt-0 rounded-md px-2.5 py-2 text-[11px] font-bold uppercase tracking-wider text-white select-none"
                        style={{ backgroundColor: "rgba(139, 92, 246, 0.75)", borderLeft: "3px solid #ede9fe" }}
                      >
                        {o.label}
                      </SelectPrimitive.Label>
                      {o.options.map((opt) => (
                        <StyledSelectItem key={String(opt.value)} value={opt.value} disabled={opt.disabled}>
                          {opt.label}
                        </StyledSelectItem>
                      ))}
                    </SelectPrimitive.Group>
                  ) : (
                    <StyledSelectItem key={String(o.value)} value={o.value} disabled={o.disabled}>
                      {o.label}
                    </StyledSelectItem>
                  )
                )
              : searchable && q ? (
                  <div className="px-3 py-6 text-center text-sm text-gray-500">No matches</div>
                ) : (
                  children
                )}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}

// Exported for callers that need richer items (icons, custom layout) via children.
export const StyledSelectItem = React.forwardRef(
  ({ className, children, value, ...props }, ref) => (
    <SelectPrimitive.Item
      ref={ref}
      value={toRadix(value)}
      className={cn(
        "relative flex w-full cursor-pointer select-none items-center rounded-lg py-2 pl-8 pr-3 " +
          "text-sm text-gray-200 outline-none transition-colors " +
          "focus:bg-violet-500/15 focus:text-white data-[state=checked]:text-white " +
          "data-[disabled]:pointer-events-none data-[disabled]:opacity-40",
        className
      )}
      {...props}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check className="h-4 w-4 text-violet-400" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  )
);
StyledSelectItem.displayName = "StyledSelectItem";
